export type AudioDurationResolver = (url: string) => Promise<number>;

export function usePositiveAudioDurationOrDefault(value: number, defaultValue: number): number {
  return Number.isFinite(value) && value > 0 ? value : defaultValue;
}

export async function resolveAudioDurationWithFallbacks(
  url: string,
  resolvers: AudioDurationResolver[],
  defaultDuration: number
): Promise<number> {
  for (const resolveDuration of resolvers) {
    try {
      const duration = await resolveDuration(url);
      if (Number.isFinite(duration) && duration > 0) return duration;
    } catch {
      // Continue with the next resolver.
    }
  }

  return defaultDuration;
}

export async function resolveAudioDurationMilliseconds(
  storedDuration: number,
  url: string,
  resolveDuration: AudioDurationResolver,
  defaultDuration: number
): Promise<number> {
  if (Number.isFinite(storedDuration) && storedDuration > 0) return storedDuration;

  let probedDuration = 0;
  try {
    probedDuration = Math.round((await resolveDuration(url)) * 1000);
  } catch {
    // Use the default below.
  }

  return usePositiveAudioDurationOrDefault(probedDuration, defaultDuration);
}

async function getAudioDurationWithMediabunny(url: string, timeoutMs: number): Promise<number> {
  const { ALL_FORMATS, Input, UrlSource } = await import('mediabunny');
  const input = new Input({
    source: new UrlSource(url, { parallelism: 1 }),
    formats: ALL_FORMATS
  });

  return new Promise((resolve) => {
    let done = false;
    const finish = (duration: number) => {
      if (done) return;
      done = true;
      clearTimeout(timer);
      input.dispose();
      resolve(duration);
    };
    const timer = setTimeout(() => finish(0), timeoutMs);

    void (async () => {
      try {
        const metadataDuration = await input.getDurationFromMetadata();
        const duration =
          metadataDuration !== null && metadataDuration > 0
            ? metadataDuration
            : await input.computeDuration();
        finish(duration);
      } catch {
        finish(0);
      }
    })();
  });
}

function getAudioDurationWithHtmlAudio(url: string, timeoutMs: number): Promise<number> {
  return new Promise((resolve) => {
    if (typeof Audio === 'undefined') {
      resolve(0);
      return;
    }

    const audio = new Audio();
    audio.preload = 'metadata';

    let done = false;
    const finish = (duration: number) => {
      if (done) return;
      done = true;
      clearTimeout(timer);
      audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
      audio.removeEventListener('error', handleError);
      audio.removeAttribute('src');
      audio.load();
      resolve(duration);
    };
    const handleLoadedMetadata = () => finish(audio.duration);
    const handleError = () => finish(0);
    const timer = setTimeout(() => finish(0), timeoutMs);

    audio.addEventListener('loadedmetadata', handleLoadedMetadata);
    audio.addEventListener('error', handleError);
    audio.src = url;
    audio.load();
  });
}

/**
 * 依次使用媒体文件解析和 HTMLAudioElement 探测音频时长（秒）。
 * 两种方式都失败或超时时返回 0，由调用方使用默认时长兜底。
 */
export function getAudioDurationFromUrl(url: string, timeoutMs = 12000): Promise<number> {
  return resolveAudioDurationWithFallbacks(
    url,
    [
      (targetUrl) => getAudioDurationWithMediabunny(targetUrl, timeoutMs),
      (targetUrl) => getAudioDurationWithHtmlAudio(targetUrl, timeoutMs)
    ],
    0
  );
}
